import React, { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin } from "lucide-react";
import { Toaster } from "sonner";

const WA = "3547883999";
const EMAIL = "info@icefleet.is";

function IceFleetWordmark({ compact = false }) {
  return (
    <div className="leading-none">
      <div className="flex items-baseline gap-0.5 font-heading font-semibold tracking-[0.18em] text-slate-900">
        <span className={compact ? "text-lg" : "text-[1.32rem] md:text-[1.55rem]"}>ICE</span>
        <span className={compact ? "text-lg text-[#0284C7]" : "text-[1.32rem] md:text-[1.55rem] text-[#0284C7]"}>FLEET</span>
      </div>
      {!compact && <p className="hidden md:block mt-1 text-[10px] font-semibold uppercase tracking-[0.38em] text-slate-500">Private Transport</p>}
    </div>
  );
}

const NAV = [
  { to: "/", label: "Home" },
  { to: "/rentals", label: "Rentals" },
  { to: "/transfers", label: "Transfers" },
  { to: "/tours", label: "Tours" },
  { to: "/long-term", label: "Long-Term" },
  { to: "/contact", label: "Contact" },
];

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans">
      <header
        data-testid="site-header"
        className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-slate-200/70"
      >
        <div className="mx-auto max-w-7xl flex items-center justify-between px-5 sm:px-8 h-16 md:h-[72px]">
          <Link to="/" className="flex items-center text-slate-900 leading-none min-h-[42px]" data-testid="nav-logo">
            <div className="max-h-[42px] md:max-h-[52px] flex items-center">
              <IceFleetWordmark />
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive ? "text-slate-900 bg-slate-100" : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/rentals"
              data-testid="nav-book-cta"
              className="ml-3 inline-flex items-center rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 transition-colors"
            >
              Check Availability
            </Link>
          </nav>
          <button
            className="md:hidden p-2 rounded-md hover:bg-slate-100 self-center"
            onClick={() => setOpen((v) => !v)}
            aria-label="toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {open && (
          <div className="md:hidden border-t border-slate-200 bg-white">
            <div className="px-5 py-3 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                  className={({ isActive }) =>
                    `px-3 py-3 rounded-md text-sm font-medium ${
                      isActive ? "bg-slate-100 text-slate-900" : "text-slate-700"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/rentals"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex justify-center rounded-md bg-slate-900 px-4 py-3 text-sm font-semibold text-white"
                data-testid="mobile-book-cta"
              >
                Check Availability
              </Link>
            </div>
          </div>
        )}
      </header>

      <main key={pathname}>
        <Outlet />
      </main>

      <footer className="mt-24 border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14 grid gap-10 md:grid-cols-4">
          <div>
            <div className="mb-3 leading-none" data-testid="footer-logo">
              <IceFleetWordmark compact />
            </div>
            <p className="text-sm text-slate-600 max-w-xs">
              Premium airport transfers, private drivers, and custom Iceland tours with professional local service.
            </p>
            <p className="mt-3 max-w-sm text-xs leading-5 text-slate-500" data-testid="footer-legal-entity-note">
              IceFleet is a trading name / brand operated by FONDO ehf., kt. 521216-1310.
            </p>
          </div>
          <div>
            <h4 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-900 mb-3">
              Services
            </h4>
            <ul className="text-sm text-slate-600 space-y-2">
              <li><Link to="/rentals" className="hover:text-slate-900">Van Rental</Link></li>
              <li><Link to="/transfers" className="hover:text-slate-900">Airport Transfers</Link></li>
              <li><Link to="/tours" className="hover:text-slate-900">Private Tours</Link></li>
              <li><Link to="/long-term" className="hover:text-slate-900">Long-Term Hire</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-900 mb-3">
              Contact
            </h4>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-center gap-2"><Phone size={14} /> +{WA}</li>
              <li className="flex items-center gap-2"><Mail size={14} /> {EMAIL}</li>
              <li className="flex items-center gap-2"><MapPin size={14} /> Reykjavík, Iceland</li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-900 mb-3">
              Company
            </h4>
            <ul className="text-sm text-slate-600 space-y-2">
              <li><Link to="/terms" className="hover:text-slate-900">Terms & Conditions</Link></li>
              <li><Link to="/privacy" className="hover:text-slate-900">Privacy Policy</Link></li>
              <li><Link to="/refund-policy" className="hover:text-slate-900">Refund Policy</Link></li>
              <li><Link to="/contact" className="hover:text-slate-900">Contact</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-200 py-6 text-center text-xs text-slate-500">
          © 2026 IceFleet. Operated by FONDO ehf. All rights reserved.
        </div>
      </footer>

      <WhatsAppFloat />
      <Toaster position="top-right" richColors />
    </div>
  );
}

export function WhatsAppFloat() {
  const msg = encodeURIComponent("Hi 👋\nThanks for your request.\n\nPlease send:\n• Pickup location\n• Drop-off location\n• Date & time\n• Number of passengers\n\nI'll confirm the price immediately 👍");
  return (
    <a
      href={`https://wa.me/${WA}?text=${msg}`}
      target="_blank"
      rel="noreferrer"
      data-testid="whatsapp-float-button"
      className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-3 text-white shadow-lg hover:shadow-xl transition-shadow"
      aria-label="Chat on WhatsApp"
    >
      <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
        <path d="M17.5 14.4c-.3-.1-1.7-.9-2-1s-.5-.1-.7.1-.8 1-.9 1.2c-.2.2-.3.2-.6.1-1.8-.9-3-1.6-4.2-3.7-.3-.5.3-.5.9-1.6.1-.2.1-.4 0-.6s-.7-1.7-1-2.3c-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1.1 1.1-1.1 2.7s1.1 3.1 1.3 3.4c.2.2 2.3 3.5 5.5 4.9.8.3 1.4.5 1.9.7.8.2 1.5.2 2.1.1.6-.1 1.7-.7 2-1.4.3-.7.3-1.3.2-1.4-.1-.1-.3-.2-.6-.3zM12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.4 1.3 4.8L2 22l5.3-1.4c1.3.7 2.9 1.1 4.7 1.1 5.5 0 10-4.5 10-10S17.5 2 12 2z" />
      </svg>
      <span className="hidden sm:inline text-sm font-semibold">WhatsApp</span>
    </a>
  );
}
